import { useCallback } from 'react';
import { useAuthStore } from '../store/authStore';
import { useAuthGateStore } from '../store/authGateStore';
import { useUIStore } from '../store/uiStore';
import { ANON_WRITES_ENABLED, anonWritesBlocked } from '../constants/auth';
import { createPrefixedLogger } from '../utils/logger';

const log = createPrefixedLogger('auth:ensureScout');

/**
 * "Ensure there's a user, then run callback." If the user is already
 * authenticated (anonymous or full), runs the callback immediately. If not,
 * silently creates an anonymous Fire Scout account and then runs it. Only
 * falls back to the full auth-gate sheet if anonymous sign-in itself fails.
 *
 * This is the lowest-friction entry point for actions a brand-new user
 * should be able to take with one tap — e.g. creating an observation. The
 * standard `useAuthGate` is still right for actions that should require an
 * intentional signup (e.g., adding a property, which carries ownership).
 *
 * While ANON_WRITES_ENABLED is false the silent scout sign-in is off:
 * signed-out users get the auth gate (which also hides its Scout CTA), and
 * an existing anonymous user gets the upgrade sheet instead of the callback.
 */
export function useEnsureScout() {
  const ensureScoutThen = useCallback(async (callback: () => void) => {
    const { user, signInAsScout } = useAuthStore.getState();
    if (anonWritesBlocked(user)) {
      useUIStore.getState().openSheet('upgradeAccount');
      return;
    }
    if (user) {
      callback();
      return;
    }
    if (!ANON_WRITES_ENABLED) {
      useAuthGateStore.getState().open(callback, {
        message: 'Sign in or create an account to post',
      });
      return;
    }
    try {
      await signInAsScout();
      callback();
    } catch (error) {
      log.warn('Silent scout sign-in failed, falling back to auth gate', error);
      useAuthGateStore.getState().open(callback, {
        message: 'Sign in or continue as a Fire Scout to post',
      });
    }
  }, []);

  return { ensureScoutThen };
}
